import React, { useState, useRef, useEffect } from "react";
import { StyleSheet, FlatList, useWindowDimensions, KeyboardAvoidingView, Platform, View, Text, Modal, Linking } from "react-native";
import ChatBubbleAI from "../components/ChatBubbleAI";
import ChatBubbleUser from "../components/ChatBubbleUser";
import ChatbotHeader from "../components/ChatbotHeader";
import ChatInputBar from "../components/ChatInputBar";
import { getAiResponse, getArticleAiResponse, getFaqs } from "../../../services/apiService";
import { useNavigation } from "@react-navigation/native";
import AppLoader from "../../../components/AppLoader";
import getGlobalStyles from "../../../globalStyles";
import { Trans, useTranslation } from "react-i18next";
import { useModalStore } from "../../../store/modalStore";
import { useKeyboardHeight } from "../../../hooks/useKeyboardHeight";
import { trackInteraction } from "../../../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../../../analytics/types";
import { ROUTES } from "../../../shared/constants/routes";
import { useAiConsentStore } from "../../../store/aiConsentStore";
import { ButtonVariant, CustomButton } from "../../../components/CustomButton";
import { useThemeSettings } from "../../../ThemeProvider";

interface ChatMessage {
  id: string;
  type: "ai" | "user";
  text: string;
  news?: any[],
  faqs?: any[];
}

const AIAssistant: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const gStyles = getGlobalStyles();
  const modalProps: any = useModalStore((state) => state.modalAdditionalProps);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const flatListRef = useRef<FlatList>(null);
  const navigation = useNavigation<any>();
  const keyboardHeight = useKeyboardHeight();
  const [isInputField, setIsInputField] = useState(false)

  const { height: SCREEN_HEIGHT } = useWindowDimensions()

  const [isFetching, setIsFetching] = useState(false);
  const hasConsented = useAiConsentStore((state) => state.hasConsented);
  const setConsented = useAiConsentStore((state) => state.setConsented);
  const { t } = useTranslation();
  const { theme } = useThemeSettings();

  // Scroll to bottom when messages update
  useEffect(() => {
    flatListRef.current?.scrollToEnd({ animated: true });
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true });
    }, 250);
  }, [messages]);


  useEffect(() => {
    async function fetchArticle() {
      if (modalProps?.articleId) {
        setIsFetching(true);
        const faqs: any = await getFaqs(modalProps?.articleId);
        const aiMsg: ChatMessage = {
          id: Date.now().toString() + "_ai_" + Math.random().toString(36).substring(7),
          type: "ai",
          text: t("screens.assistant.faqBotIntro"),
          news: [],
          faqs: faqs?.faqs || [],
        };
        setIsFetching(false);
        setMessages((prev) => [...prev, aiMsg]);
      }
    }
    if (!modalProps?.showGeneralAssistant && modalProps?.articleId) {
      fetchArticle()
    }/*  else {
      if (messages.length === 0) {
        const newAiMsg: ChatMessage = {
          id: Date.now().toString(),
          type: "ai",
          text: t(`screens.assistant.botIntro${Math.floor(Math.random() * 4) + 1}`),
        };
        setMessages([newAiMsg]);
      }
    }; */
  }, [modalProps?.articleId, modalProps?.showGeneralAssistant, navigation]);

  const renderAiResponse = (response: any) => {
    if (response && response.answer) {
      const aiMsg: ChatMessage = {
        id: Date.now().toString() + "_ai_" + Math.random().toString(36).substring(7),
        type: "ai",
        text: response.answer,
        news: response.references,
      };
      // console.log("AI Message with FAQs:", aiMsg);
      setIsFetching(false);
      setMessages((prev) => [...prev, aiMsg]);
    }
  }

  const onFaqSelect = async (faq: any) => {
    const newUserMsg: ChatMessage = {
      id: Date.now().toString(),
      type: "user",
      text: faq,
    };
    setIsFetching(true);
    setMessages((prev) => [...prev, newUserMsg]);
    const response = await getArticleAiResponse(modalProps?.articleId, faq);
    renderAiResponse(response);
  };
    const openPrivacyPolicy = () => {
    // Close the assistant sheet first so Privacy comes to front.
    onClose();
    setTimeout(() => {
      navigation.navigate(ROUTES.PRIVACY);
    }, 50);
  };

  const handleSend = async (text: string, label?: string) => {
    if (!isFetching && text?.trim()) {
      const newUserMsg: ChatMessage = {
        id: Date.now().toString(),
        type: "user",
        text: label || text,
      };
      setIsFetching(true);
      setMessages((prev) => [...prev, newUserMsg]);
      let response: any;

      if (modalProps?.showGeneralAssistant) {
        response = await getAiResponse(text);
      }
      else {
        response = await getArticleAiResponse(modalProps?.articleId, text);
      }
      renderAiResponse(response);
    }
  };

  const renderItem = ({ item }: { item: ChatMessage }) => {
    if (item.type === "ai") {
      return <ChatBubbleAI message={item.text} newsItems={item.news} faqs={item.faqs} faqSelect={onFaqSelect} />;
    }
    return <ChatBubbleUser message={item.text} />;
  };
    const consentTransLinks = {
    openaiLink: (
      <Text
        style={{ fontWeight: "700", textDecorationLine: "underline" }}
        onPress={() => Linking.openURL("https://openai.com/enterprise-privacy/")}
      />
    ),
  };

  const consentMessage = (
    <Text style={[gStyles.subtitle, { textAlign: "center", color: theme.colors.textPrimary }]}>
      <Trans i18nKey="screens.assistant.consentMessage" components={consentTransLinks} />
    </Text>
  );


  return (
    <KeyboardAvoidingView style={[gStyles.fullWidthWithMargin, gStyles.centerColumn, {
      height: SCREEN_HEIGHT - (Platform.OS === 'web' ? (isInputField ? 120 : 160) : 200),
      paddingBottom: Platform.OS === "android" ? keyboardHeight : 0,
    }]} behavior={Platform.OS === "ios" ? "padding" : undefined} keyboardVerticalOffset={Platform.OS === "ios" ? 80 : 0}>
      {/* Header */}
      <ChatbotHeader onClose={onClose} />

      {/* Chat messages */}
      <FlatList
        ref={flatListRef}
        data={messages}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingVertical: 12 }}
        style={styles.list}
      />
      {isFetching && <AppLoader size={24} style={{ paddingBottom: 12, height: 30 }} isLoading />}

      {/* Input Bar */}
      <ChatInputBar onSend={handleSend} showSuggestions={modalProps?.showGeneralAssistant} onFocusChange={setIsInputField}/>
      {keyboardHeight === 0 && !(Platform.OS === "web" && isInputField) && (
        <Text style={[styles.disclaimerText, { color: gStyles?.header3?.color ?? "#777" }]}>
          <Trans i18nKey="screens.assistant.linkToPrivacyPolicy" components={{ openaiLink: (<Text onPress={openPrivacyPolicy} style={{ fontWeight: "700", textDecorationLine: "underline" }}/>)}}/>
        </Text>
      )}
    </KeyboardAvoidingView>
  );
};

export default AIAssistant;

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  consentBackdrop: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    zIndex: 1001,
  },
  consentCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 20,
    gap: 12,
  },
  disclaimerText: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 16,
    textAlign: "center",
  },
});